import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ChangeEvent,
} from 'react';

import HeaderContext from '../context/HeaderContext';
import HeaderNavBarContext from '../context/HeaderNavBarContext';
import HeaderSearchBarContext from '../context/HeaderSearchBarContext';

import fireEvent from '../utils/fireEvent';
import { getUserPostcode, setUserPostcode } from '../utils/userPostcode';
import fetchApI from '../utils/fetchApi';

import TextField from '../../../components/TextField/TextField';
import Dropdown from '../../../components/Dropdown/Dropdown';
import { SecondaryButton } from '../../../components/Button';
import Icon from '../../../components/Icon/Icon';
import Autocomplete from '../../../components/Autocomplete/Autocomplete';
import getModifiedSavedSearches from './utils/getModifiedSavedSearches';

type TSearchBar = {
  setExpandSearchBar: Dispatch<SetStateAction<boolean>>;
  trackGAEvent?: (eventName: string, eventAction: string) => void;
  searchBarTranslationsForTenant?: Record<string, string>;
};

type TSearchSuggestionItem = {
  modifiers: string[];
  value: string;
  searchLink: string;
};

const SearchBar = ({ setExpandSearchBar, trackGAEvent, searchBarTranslationsForTenant }: TSearchBar) => {
  const { xsrfToken } = useContext<any>(HeaderContext);
  const {
    userDetails: { isLoggedIn },
  } = useContext<any>(HeaderNavBarContext);
  const searchFormRef = useRef<HTMLFormElement>(null);

  const {
    action,
    categoryValue,
    categoryOptions,
    distanceValue,
    distanceOptions,
    hasSearchOnPage,
    searchOnPageValue,
    hasSearchOnTitleAndDescription = true,
    searchOnTitleAndDescriptionValue,
    postcodeValue,
    queryValue,
    recentQueryUrl,
    savedSearchesUrl,
    recentClearUrl,
    suggestionsUrl,
    isSimpleBar,
    translations,
    suggestionRedirectDisabled = false,
  } = useContext<any>(HeaderSearchBarContext);

  const [isSearchOnPage, setIsSearchOnPage] = useState(searchOnPageValue);
  const [isSearchOnTitleAndDescription, setIsSearchOnTitleAndDescription] = useState(searchOnTitleAndDescriptionValue);
  const [category, setCategory] = useState(categoryValue);
  const [distance, setDistance] = useState(distanceValue);
  const [postcode, setPostcode] = useState('');
  const [suggestionList, setSuggestionList] = useState([]);
  const [savedSearchesList, setSavedSearchesList] = useState([]);
  const [autocompleteValue, setAutocompleteValue] = useState(queryValue);
  const [isHardSubmit, setIsHardSubmit] = useState(false);

  const handleSearchFormSubmit = useCallback(
    (event) => {
      setUserPostcode(postcode);

      // fix values
      const searchEvent = fireEvent(
        'search',
        {
          category,
          query: autocompleteValue,
          postcode,
          distance,
          onTitleAndDescription: Boolean(isSearchOnTitleAndDescription),
          onPage: Boolean(isSearchOnPage),
        },
        {
          cancelable: true,
        },
      );

      if (searchEvent.defaultPrevented) {
        if (event) {
          event.preventDefault();
        }
      } else if (action) {
        // @ts-ignore
        searchFormRef.current?.submit();
      }
    },
    [action, postcode, category, autocompleteValue, distance, isSearchOnTitleAndDescription, isSearchOnPage],
  );

  useEffect(() => {
    if (isLoggedIn) {
      const fetchData = async () => {
        try {
          const res = await fetchApI(recentQueryUrl);
          const result = await fetchApI(savedSearchesUrl);
          setSuggestionList(res);
          const modifiedSavedSearchesArr = getModifiedSavedSearches(result);
          setSavedSearchesList(modifiedSavedSearchesArr);
        } catch (error) {
          // eslint-disable-next-line no-console
        }
      };

      fetchData();
    }
  }, [recentQueryUrl, isLoggedIn, savedSearchesUrl]);

  useEffect(() => {
    if (isHardSubmit) {
      // eslint-disable-next-line no-undefined
      handleSearchFormSubmit(undefined);
      setIsHardSubmit(false);
    }
  }, [isHardSubmit, handleSearchFormSubmit]);

  useEffect(() => {
    setAutocompleteValue(queryValue);
  }, [queryValue]);

  useEffect(() => {
    setCategory(categoryValue);
  }, [categoryValue]);

  useEffect(() => {
    const value = getUserPostcode() || postcodeValue;
    setPostcode(value);
  }, [postcodeValue]);

  useEffect(() => {
    setDistance(distanceValue);
  }, [distanceValue]);

  useEffect(() => {
    setIsSearchOnTitleAndDescription(searchOnTitleAndDescriptionValue);
  }, [searchOnTitleAndDescriptionValue]);

  useEffect(() => {
    setIsSearchOnPage(searchOnPageValue);
  }, [searchOnPageValue]);

  const handleSearchQuerySuggestionApply = (item: TSearchSuggestionItem, isSubmit: boolean) => {
    if (trackGAEvent && item.modifiers.length) {
      if (item.modifiers[0] === 'api') {
        trackGAEvent('AutoComplete', 'SuggestionClicked');
      } else if (item.modifiers[0] === 'query') {
        trackGAEvent('AutoComplete', 'SuggestionClicked-Saved');
      } else {
        trackGAEvent('AutoComplete', 'SuggestionClicked-Recent');
      }
    }

    setAutocompleteValue(item.value);

    if (isSubmit) {
      if (item.searchLink && !suggestionRedirectDisabled) {
        document.location = item.searchLink;
      } else {
        setIsHardSubmit(isSubmit);
      }
    }
  };

  const handleAutocompleteClick = () => {
    setExpandSearchBar(true);
  };

  const handleAutocompleteChange = (value: string) => {
    setAutocompleteValue(value);
  };

  const handlePostcodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const maxLength = 7;
    setPostcode((e.target.value || '').slice(0, maxLength));
  };

  return (
    <div className="hz-Header-searchBar">
      <div className={`hz-Header-maxWidth ${isSimpleBar ? 'hz-Header-simple-searchBar' : ''}`}>
        <form
          ref={searchFormRef}
          className="hz-SearchForm"
          method="get"
          action={action}
          onSubmit={handleSearchFormSubmit}
          data-role="searchForm"
        >
          <div className="hz-SearchFieldset-standard">
            <div className="hz-SearchForm-query" data-role="searchForm-query">
              <Autocomplete
                name="query"
                spellCheck={false}
                data-role="searchForm-autocomplete"
                aria-label={translations.queryPlaceholder}
                value={autocompleteValue}
                xsrfToken={xsrfToken}
                suggestionsQueryUrl={suggestionsUrl}
                clearPredefinedSuggestionsUrl={recentClearUrl}
                predefinedSuggestions={suggestionList}
                savedSearches={savedSearchesList}
                suggestionCategory={category}
                onClick={handleAutocompleteClick}
                onChange={handleAutocompleteChange}
                onSelect={handleSearchQuerySuggestionApply}
                suggestionRedirectDisabled={suggestionRedirectDisabled}
                translations={translations}
                searchBarTranslationsForTenant={searchBarTranslationsForTenant}
              />
            </div>
          </div>
          {!isSimpleBar && (
            <div className="hz-SearchFieldset-advanced">
              <div className="hz-SearchForm-category">
                <Dropdown
                  id="categoryId"
                  value={category}
                  placeHolderText={translations.categoryPlaceholder}
                  options={categoryOptions}
                  onChange={(e) => setCategory(e.target.value)}
                  data-role="searchForm-category-menu"
                  ariaLabel={translations.categoryPlaceholder}
                />
              </div>
              <div id="formPostcode" className="hz-SearchForm-postcode">
                <TextField
                  name="postcode"
                  placeholder={translations.postcodeInputPlaceholder}
                  value={postcode}
                  ariaLabel={translations.postcodeInputPlaceholder}
                  data-role="searchForm-postcode"
                  onChange={handlePostcodeChange}
                />
              </div>
              <div className="hz-SearchForm-distance">
                <Dropdown
                  id="distance"
                  value={distance}
                  placeHolderText={translations.distancePlaceholder}
                  ariaLabel={translations.distancePlaceholder}
                  options={distanceOptions}
                  onChange={(e) => setDistance(e.target.value)}
                  data-role="searchForm-distance-menu"
                />
              </div>
            </div>
          )}
          <SecondaryButton type="submit" className="hz-SearchForm-search" data-role="searchForm-button">
            <Icon name="search" size="m" className="hz-hide-gte-md" />
            <span className="hz-show-md">{translations.searchButton}</span>
          </SecondaryButton>
          {!isSimpleBar && (
            <div className="hz-SearchForm-options">
              {hasSearchOnTitleAndDescription && (
                <label>
                  <input
                    type="checkbox"
                    name="searchOnTitleAndDescription"
                    value="true"
                    checked={isSearchOnTitleAndDescription}
                    onChange={(event) => {
                      setIsSearchOnTitleAndDescription(event.target.checked);
                    }}
                  />
                  {translations.searchInTitleAndDescription}
                </label>
              )}

              {hasSearchOnPage && (
                <label>
                  <input
                    type="checkbox"
                    name="searchOnPage"
                    value="true"
                    onChange={(event) => {
                      setIsSearchOnPage(event.target.checked);
                    }}
                  />
                  {translations.searchFromThisAdvertiser}
                </label>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default SearchBar;
